import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Divider,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  Work as WorkIcon,
  LocationOn as LocationOnIcon,
  CalendarToday as CalendarTodayIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { generatePublicJobAlertMessage } from '../../services/whatsappTemplateService';
import { useLanguage } from '../../contexts/LanguageContext';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import RegistrationModal from '../../components/RegistrationModal';

// API Base URL helper
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Qualification mapping
const QUALIFICATION_LABELS = {
  ssc: 'SSC / 10th',
  inter: 'Inter / 12th',
  degree: 'Degree',
  btech: 'B.Tech',
  iti: 'ITI / Diploma',
  nursing: 'Nursing',
  teacher: 'Teacher',
  ttc: 'TTC / B.Ed',
  pg: 'PG / Post Graduate',
  hallTicket: 'Hall Tickets',
  results: 'Results',
  admissionInfo: 'Admissions',
  counsellingInfo: 'Web Counselling Info',
};

// Category options - Qualification Categories
const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'ssc', label: 'SSC / 10th' },
  { value: 'inter', label: 'Inter / 12th' },
  { value: 'degree', label: 'Degree' },
  { value: 'btech', label: 'B.Tech' },
  { value: 'iti', label: 'ITI / Diploma' },
  { value: 'nursing', label: 'Nursing' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'ttc', label: 'TTC / B.Ed' },
  { value: 'pg', label: 'PG / Post Graduate' },
  { value: 'hallTicket', label: 'Hall Tickets' },
  { value: 'results', label: 'Results' },
  { value: 'admissionInfo', label: 'Admissions' },
  { value: 'counsellingInfo', label: 'Web Counselling Info' },
];

// Sort options
const SORT_OPTIONS = [
  { value: 'lastDate', label: 'Last Date (Ascending)' },
  { value: 'lastDateDesc', label: 'Last Date (Descending)' },
  { value: 'createdAt', label: 'Newest First' },
  { value: 'createdAtDesc', label: 'Oldest First' },
  { value: 'notificationName', label: 'Name (A-Z)' },
];

const LAST_DATE_RED = '#c62828';

const CHIP_COLORS = {
  ssc: { bg: 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)', color: '#fff' },
  inter: { bg: 'linear-gradient(135deg, #00897b 0%, #26a69a 100%)', color: '#fff' },
  degree: { bg: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)', color: '#fff' },
  btech: { bg: 'linear-gradient(135deg, #7b1fa2 0%, #512da8 100%)', color: '#fff' },
  iti: { bg: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%)', color: '#fff' },
  nursing: { bg: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)', color: '#fff' },
  teacher: { bg: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: '#fff' },
  ttc: { bg: 'linear-gradient(135deg, #8d6e63 0%, #6d4c41 100%)', color: '#fff' },
  pg: { bg: 'linear-gradient(135deg, #5e35b1 0%, #4527a0 100%)', color: '#fff' },
  hallticket: { bg: 'linear-gradient(135deg, #00acc1 0%, #00838f 100%)', color: '#fff' },
  results: { bg: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)', color: '#fff' },
  admissioninfo: { bg: 'linear-gradient(135deg, #ab47bc 0%, #8e24aa 100%)', color: '#fff' },
  counsellinginfo: { bg: 'linear-gradient(135deg, #29b6f6 0%, #0288d1 100%)', color: '#fff' },
  default: { bg: 'linear-gradient(135deg, #ff6f00 0%, #ff8f00 100%)', color: '#fff' },
};

const INFO_BOX_THEMES = {
  lastDate: { border: LAST_DATE_RED, bg: '#ffebee', icon: LAST_DATE_RED },
  vacancies: { border: '#1565c0', bg: '#e3f2fd', icon: '#1565c0' },
  qualification: { border: '#6a1b9a', bg: '#f3e5f5', icon: '#6a1b9a' },
  ageLimit: { border: '#2e7d32', bg: '#e8f5e9', icon: '#2e7d32' },
  fee: { border: '#ef6c00', bg: '#fff3e0', icon: '#ef6c00' },
  salary: { border: '#00838f', bg: '#e0f7fa', icon: '#00838f' },
  location: { border: '#5c6bc0', bg: '#e8eaf6', icon: '#5c6bc0' },
  date: { border: '#0277bd', bg: '#e1f5fe', icon: '#0277bd' },
  default: { border: '#78909c', bg: '#eceff1', icon: '#546e7a' },
};

const isMeaningfulText = (value) => {
  if (value == null) return false;
  const s = String(value).trim();
  if (!s || s === '.' || s === '-' || s === '—' || s.toLowerCase() === 'n/a' || s.toLowerCase() === 'na') {
    return false;
  }
  return true;
};

const cleanListItems = (items) => {
  if (!items) return [];
  const arr = Array.isArray(items) ? items : String(items).split(',');
  return arr.map((x) => String(x).trim()).filter(isMeaningfulText);
};

const normalizeChipKey = (key) => String(key || '').trim().toLowerCase().replace(/\s+/g, '');

const getQualificationChipKeys = (job) => {
  const keys = new Set();
  if (job?.category && job.category !== 'all') {
    keys.add(normalizeChipKey(job.category));
  }
  if (Array.isArray(job?.applicableFilters)) {
    job.applicableFilters.forEach((f) => {
      if (isMeaningfulText(f)) keys.add(normalizeChipKey(f));
    });
  }
  if (isMeaningfulText(job?.qualification)) {
    String(job.qualification)
      .split(/[,;/|]+/)
      .forEach((q) => {
        const k = normalizeChipKey(q);
        if (k) keys.add(k);
      });
  }
  return [...keys];
};

const getChipStyle = (key) => CHIP_COLORS[normalizeChipKey(key)] || CHIP_COLORS.default;

const getChipLabel = (key, t) => {
  const k = normalizeChipKey(key);
  return t(k) || QUALIFICATION_LABELS[k] || String(key).toUpperCase();
};

const QualificationChips = ({ job, size = 'small', sx = {}, t }) => {
  const keys = getQualificationChipKeys(job);
  if (!keys.length) return null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={sx}>
      {keys.map((key) => {
        const style = getChipStyle(key);
        return (
          <Chip
            key={key}
            label={getChipLabel(key, t)}
            size={size}
            sx={{
              fontWeight: 700,
              fontSize: size === 'small' ? '0.7rem' : '0.8rem',
              background: style.bg,
              color: style.color,
              border: 'none',
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            }}
          />
        );
      })}
    </Stack>
  );
};

const LastDateDisplay = ({ date, t, formatDate, getDaysRemaining, variant = 'body2', sx = {} }) => {
  if (!date) return null;
  const days = getDaysRemaining(date);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, ...sx }}>
      <CalendarTodayIcon sx={{ fontSize: 18, color: LAST_DATE_RED, mt: 0.2, flexShrink: 0 }} />
      <Typography
        variant={variant}
        sx={{ color: LAST_DATE_RED, fontWeight: 700, lineHeight: 1.4 }}
      >
        <Box component="span" sx={{ fontWeight: 800 }}>
          {t('lastDate')}:
        </Box>{' '}
        {formatDate(date)}
        {days !== null && days >= 0 && (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {' '}
            ({days} {days !== 1 ? t('daysLeft') : t('dayLeft')})
          </Box>
        )}
      </Typography>
    </Box>
  );
};

const InfoBox = ({ children, themeKey = 'default', icon: Icon }) => {
  const boxTheme = INFO_BOX_THEMES[themeKey] || INFO_BOX_THEMES.default;
  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: 2,
        borderLeft: `4px solid ${boxTheme.border}`,
        backgroundColor: boxTheme.bg,
        height: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
      }}
    >
      {Icon && <Icon sx={{ color: boxTheme.icon, mt: 0.2, flexShrink: 0 }} />}
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Box>
  );
};

const DetailSection = ({ title, children, color = '#667eea' }) => (
  <>
    <Divider sx={{ my: 2.5 }} />
    <Typography
      variant="h6"
      sx={{
        mb: 1.5,
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&::before': {
          content: '""',
          width: 4,
          height: 22,
          borderRadius: 2,
          background: color,
          display: 'inline-block',
        },
      }}
    >
      {title}
    </Typography>
    {children}
  </>
);

const PublicJobAlerts = () => {
  const { t, translateJobContent, language } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const REG_PROMPT_KEY = 'jobalerts_reg_prompt_shown';
  const [jobAlerts, setJobAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('lastDate');
  const [selectedJob, setSelectedJob] = useState(null);
  const [listMode, setListMode] = useState('active'); // active | expired
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [hasViewedAlert, setHasViewedAlert] = useState(false);
  const [hasPromptedThisSession, setHasPromptedThisSession] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem(REG_PROMPT_KEY) === 'true';
  });

  // Fetch job alerts (with fallback to authenticated route if public fails)
  const fetchJobAlerts = async () => {
    try {
      setLoading(true);
      setError('');
      const baseParams = {
        limit: 1000,
      };
      if (categoryFilter !== 'all') {
        baseParams.category = categoryFilter;
      }
      if (searchQuery) {
        baseParams.search = searchQuery;
      }

      const fetchOne = async (isActive) => {
        const params = { ...baseParams, isActive };
        try {
          const response = await axios.get(`${API_BASE_URL}/jobalerts/public`, { params });
          if (response.data?.success) return response.data.data || [];
        } catch (publicErr) {
          const token = localStorage.getItem('token');
          if (token) {
            const response = await axios.get(`${API_BASE_URL}/jobalerts`, {
              params,
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data?.success) return response.data.data || [];
          }
          throw publicErr;
        }
        return [];
      };

      // Active for main list; inactive (auto-expired) for Expired tab
      const [activeRows, inactiveRows] = await Promise.all([
        fetchOne('true'),
        fetchOne('false'),
      ]);
      const byId = new Map();
      [...activeRows, ...inactiveRows].forEach((row) => {
        if (row?.id) byId.set(row.id, row);
      });
      setJobAlerts([...byId.values()]);
    } catch (err) {
      console.error('Job alerts fetch failed:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load job alerts');
      setJobAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/jobalerts/stats/summary`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  useEffect(() => {
    fetchJobAlerts();
    fetchStats();
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing user:', e);
      }
    }
  }, [categoryFilter, searchQuery]);

  // Check for job ID in URL and open that job alert
  useEffect(() => {
    if (jobAlerts.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const jobId = urlParams.get('job');
      if (jobId) {
        const job = jobAlerts.find((j) => j.id === jobId);
        if (job) {
          setSelectedJob(job);
          if (!user && !hasViewedAlert) {
            setHasViewedAlert(true);
          }
          // Remove the parameter from URL after opening
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    }
  }, [jobAlerts]);

  // Get days remaining
  const getDaysRemaining = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const isExpiredAlert = (job) => {
    if (!job?.lastDate) return false;
    const days = getDaysRemaining(job.lastDate);
    return days !== null && days < 0;
  };

  // Filter and sort job alerts
  const filteredAndSortedAlerts = useMemo(() => {
    let filtered = [...jobAlerts];

    if (listMode === 'expired') {
      filtered = filtered.filter(isExpiredAlert);
    } else {
      // Active list: still open (not past last date). Backend also auto-inactivates.
      filtered = filtered.filter((job) => job.isActive !== false && !isExpiredAlert(job));
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.notificationName?.toLowerCase().includes(query) ||
          job.organization?.toLowerCase().includes(query) ||
          job.description?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'lastDate':
          if (!a.lastDate && !b.lastDate) return 0;
          if (!a.lastDate) return 1;
          if (!b.lastDate) return -1;
          return new Date(a.lastDate) - new Date(b.lastDate);
        case 'lastDateDesc':
          if (!a.lastDate && !b.lastDate) return 0;
          if (!a.lastDate) return 1;
          if (!b.lastDate) return -1;
          return new Date(b.lastDate) - new Date(a.lastDate);
        case 'createdAt':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'createdAtDesc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'notificationName':
          return (a.notificationName || '').localeCompare(b.notificationName || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [jobAlerts, searchQuery, sortBy, listMode]);

  // Handle job card click
  const handleJobClick = (job) => {
    if (!user && hasViewedAlert && !hasPromptedThisSession) {
      setRegistrationOpen(true);
      setHasPromptedThisSession(true);
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(REG_PROMPT_KEY, 'true');
      }
      return;
    }
    setSelectedJob(job);
    if (!user && !hasViewedAlert) {
      setHasViewedAlert(true);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedJob(null);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get qualification stats
  const qualificationStats = useMemo(() => {
    if (!stats?.byQualification) return [];
    return Object.entries(stats.byQualification)
      .map(([key, count]) => ({
        qualification: key,
        label: QUALIFICATION_LABELS[key] || key.charAt(0).toUpperCase() + key.slice(1),
        count,
      }))
      .filter((stat) => stat.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [stats]);

  // Share functions
  const generateShareText = (job) => {
    // Use production URL for share links (clickable in messaging apps)
    const getShareUrl = () => {
      const baseUrl = window.location.href.includes('localhost') || window.location.href.includes('127.0.0.1')
        ? 'https://services.knls.in/jobalerts'
        : `${window.location.origin}${window.location.pathname}`;
      // Add job ID parameter to open specific job alert directly
      return `${baseUrl}?job=${job.id}`;
    };
    
    const jobUrl = getShareUrl();
    const categoryLabel = t(job.category) || QUALIFICATION_LABELS[job.category] || job.category;
    const lastDateText = job.lastDate ? `${t('lastDate')}: ${formatDate(job.lastDate)}` : '';
    const daysRemaining = getDaysRemaining(job.lastDate);
    const daysText = daysRemaining !== null && daysRemaining >= 0 
      ? ` (${daysRemaining} ${daysRemaining !== 1 ? t('daysLeft') : t('dayLeft')})` 
      : '';
    
    const basePageUrl = window.location.href.includes('localhost') || window.location.href.includes('127.0.0.1')
      ? 'https://services.knls.in/jobalerts'
      : `${window.location.origin}${window.location.pathname}`;
    
    const creatorShopName = String(job.creatorShopName || '').trim() || 'KNLS Computers';
    const creatorPhone = String(job.creatorPhone || '').replace(/\D/g, '').slice(-10) || '9885750476';

    // Creator shop/phone only — never the viewer's WhatsApp tenant cache
    return generatePublicJobAlertMessage({
      jobTitle: translateJobContent(job.notificationName),
      category: categoryLabel,
      organization: translateJobContent(job.organization || 'N/A'),
      vacancies: job.numberOfVacancies ? `📊 ${t('vacancies')}: ${job.numberOfVacancies}\n` : '',
      qualification: job.qualification ? `🎓 ${t('qualification')}: ${translateJobContent(job.qualification)}\n` : '',
      lastDate: lastDateText ? `📅 ${lastDateText}${daysText}\n` : '',
      location: job.location ? `📍 ${t('location')}: ${translateJobContent(job.location)}\n` : '',
      salary: job.salary ? `💰 ${t('salary')}: ${translateJobContent(job.salary)}\n` : '',
      ageLimit: job.ageLimit ? `👤 ${t('ageLimit')}: ${translateJobContent(job.ageLimit)}\n` : '',
      jobUrl: jobUrl,
      basePageUrl: basePageUrl,
      categoryHashtag: categoryLabel.replace(/\s+/g, ''),
      businessName: creatorShopName,
      shopName: creatorShopName,
      businessPhone: creatorPhone,
    });
  };

  const shareOnWhatsApp = (job) => {
    const text = generateShareText(job);
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareOnTelegram = (job) => {
    // Use production URL for Telegram (clickable links) with job ID
    const getShareUrl = () => {
      const baseUrl = window.location.href.includes('localhost') || window.location.href.includes('127.0.0.1')
        ? 'https://services.knls.in/jobalerts'
        : `${window.location.origin}${window.location.pathname}`;
      return `${baseUrl}?job=${job.id}`;
    };
    
    const shareUrl = getShareUrl();
    const text = generateShareText(job);
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareViaEmail = (job) => {
    const subject = encodeURIComponent(`Job Alert: ${translateJobContent(job.notificationName)}`);
    const body = encodeURIComponent(generateShareText(job));
    const url = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = url;
  };

  const handleRegistrationSuccess = (userData) => {
    setUser(userData);
    setRegistrationOpen(false);
    // Refresh job alerts after registration
    fetchJobAlerts();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 1, sm: 2, md: 3 },
        px: { xs: 0, sm: 0 },
      }}
    >
      {/* Registration Modal */}
      <RegistrationModal
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
        onSuccess={handleRegistrationSuccess}
      />
      <Container 
        maxWidth="xl"
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
        }}
      >
        {/* Header with Language Switcher */}
        <Box 
          sx={{ 
            mb: { xs: 1.5, sm: 2, md: 3 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 1,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
            }}
          >
            <Box component="span" sx={{ color: 'white' }}>KNLS</Box>
            <Box component="span" sx={{ mx: 0.5, color: 'rgba(255,255,255,0.8)' }}>-</Box>
            <Box component="span">Job Alerts ({t('jobAlerts')})</Box>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 1 }}>
            <LanguageSwitcher useSettingsIcon={false} />
          </Box>
        </Box>

        {/* Language Change Notice - Just Language Options */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            background: 'white',
            borderRadius: { xs: 2, sm: 2.5 },
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <LanguageSwitcher variant="chips" />
          </Box>
        </Paper>

        {/* Statistics Section */}
        {stats && (
          <Paper
            elevation={3}
            sx={{
              p: { xs: 2.5, sm: 3 },
              mb: { xs: 3, sm: 4 },
              background: 'white',
              borderRadius: { xs: 2.5, sm: 3 },
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                mb: { xs: 2, sm: 2.5 }, 
                fontWeight: 'bold',
                fontSize: { xs: '1.125rem', sm: '1.25rem' },
              }}
            >
              <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
              {t('currentStatistics')}
            </Typography>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: { xs: 2.5, sm: 2.75, md: 2 },
                    borderRadius: { xs: 2.5, sm: 2 },
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    minHeight: { xs: 100, sm: 90 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 'bold',
                      fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
                    }}
                  >
                    {stats.active || 0}
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      mt: 0.5,
                    }}
                  >
                    Active Jobs
                  </Typography>
                </Box>
              </Grid>
              {qualificationStats.map((stat) => (
                <Grid item xs={6} sm={4} md={2} key={stat.qualification}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: { xs: 2, sm: 2.25, md: 2 },
                      borderRadius: { xs: 2.5, sm: 2 },
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      minHeight: { xs: 100, sm: 90 },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4,
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                    onClick={() => setCategoryFilter(stat.qualification)}
                  >
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                      }}
                    >
                      {stat.count}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                        mt: 0.5,
                        lineHeight: 1.2,
                      }}
                    >
                      {t(stat.qualification) || stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Category Tabs - Single Line */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 0.5, sm: 1 },
            mb: { xs: 1.5, sm: 2, md: 3 },
            background: 'white',
            borderRadius: { xs: 2, sm: 2.5 },
          }}
        >
          {/* Purpose Description */}
          <Typography
            variant="subtitle1"
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.25 },
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            🔍 Search for:
          </Typography>
          <Tabs
            value={categoryFilter}
            onChange={(_, value) => setCategoryFilter(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 40, sm: 48 },
              '& .MuiTab-root': {
                minHeight: { xs: 40, sm: 48 },
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
                px: { xs: 1, sm: 1.5, md: 2 },
                py: { xs: 0.75, sm: 1 },
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 700,
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              },
            }}
          >
            {CATEGORIES.map((cat) => (
              <Tab
                key={cat.value}
                value={cat.value}
                label={cat.value === 'all' ? 'All' : (t(cat.value) || cat.label)}
              />
            ))}
          </Tabs>
        </Paper>

        {/* Search and Sort */}
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            mb: { xs: 2, sm: 3, md: 4 },
            background: 'white',
            borderRadius: { xs: 2.5, sm: 3 },
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder={t('searchJobs')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size={isMobile ? 'small' : 'medium'}
                InputProps={{
                  startAdornment: <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>{t('sortBy')}</InputLabel>
                <Select
                  value={sortBy}
                  label={t('sortBy')}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.value) || option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={listMode}
                onChange={(_, next) => {
                  if (next) setListMode(next);
                }}
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="expired">Expired / Inactive</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </Paper>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={60} />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {/* Job Alerts Grid */}
        {!loading && !error && (
          <>
            <Typography variant="h6" sx={{ mb: 2, color: 'white' }}>
              {filteredAndSortedAlerts.length} {filteredAndSortedAlerts.length !== 1 ? t('jobsFound') : t('jobFound')}
            </Typography>
            {filteredAndSortedAlerts.length === 0 ? (
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: 'white',
                  borderRadius: 3,
                }}
              >
                <InfoIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('noJobAlertsFound')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('tryAdjustingFilters')}
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {filteredAndSortedAlerts.map((job) => {
                  const daysRemaining = getDaysRemaining(job.lastDate);
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={job.id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          borderTop: '4px solid #667eea',
                          overflow: 'hidden',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 6,
                          },
                        }}
                        onClick={() => handleJobClick(job)}
                      >
                        <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <QualificationChips job={job} t={t} size="small" sx={{ flex: 1 }} />
                            {job.isUrgent && (
                              <Chip
                                label={t('urgent')}
                                size="small"
                                color="error"
                                sx={{ fontWeight: 'bold', ml: 0.5, flexShrink: 0 }}
                              />
                            )}
                          </Box>
                          <Typography
                            variant="h6"
                            component="h3"
                            sx={{
                              fontWeight: 'bold',
                              mb: 0.75,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              fontSize: { xs: '0.95rem', sm: '1.05rem' },
                            }}
                          >
                            {translateJobContent(job.notificationName)}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1, fontWeight: 500 }}
                          >
                            {translateJobContent(job.organization)}
                          </Typography>
                          {isMeaningfulText(job.description) && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                mb: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.4,
                              }}
                            >
                              {translateJobContent(job.description)}
                            </Typography>
                          )}
                          {job.lastDate && (
                            <Box
                              sx={{
                                mb: 1,
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: '#ffebee',
                                border: '1px solid rgba(198,40,40,0.25)',
                              }}
                            >
                              <LastDateDisplay
                                date={job.lastDate}
                                t={t}
                                formatDate={formatDate}
                                getDaysRemaining={getDaysRemaining}
                                variant="caption"
                              />
                            </Box>
                          )}
                          <Stack spacing={0.5} sx={{ mb: 0.5 }}>
                            {job.numberOfVacancies > 0 && (
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1565c0' }}>
                                <WorkIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.4 }} />
                                {t('vacancies')}: {job.numberOfVacancies}
                              </Typography>
                            )}
                            {isMeaningfulText(job.ageLimit) && (
                              <Typography variant="caption" color="text.secondary">
                                {t('ageLimit')}: {translateJobContent(job.ageLimit)}
                              </Typography>
                            )}
                            {isMeaningfulText(job.qualification) && !getQualificationChipKeys(job).length && (
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#6a1b9a' }}>
                                <SchoolIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.4 }} />
                                {translateJobContent(job.qualification)}
                              </Typography>
                            )}
                          </Stack>
                          {job.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <LocationOnIcon sx={{ fontSize: 16, mr: 0.5, color: '#5c6bc0' }} />
                              <Typography variant="caption" color="text.secondary">
                                {translateJobContent(job.location)}
                              </Typography>
                            </Box>
                          )}
                          {daysRemaining !== null && daysRemaining >= 0 && (
                            <Chip
                              label={`${daysRemaining} ${daysRemaining !== 1 ? t('daysLeft') : t('dayLeft')}`}
                              size="small"
                              sx={{
                                mt: 0.5,
                                fontWeight: 700,
                                bgcolor: daysRemaining <= 7 ? '#ffcdd2' : daysRemaining <= 30 ? '#ffe0b2' : '#c8e6c9',
                                color: daysRemaining <= 7 ? '#b71c1c' : daysRemaining <= 30 ? '#e65100' : '#1b5e20',
                              }}
                            />
                          )}
                        </CardContent>
                        <CardActions sx={{ flexDirection: 'column', gap: 1, p: 1.5 }}>
                          <Button 
                            size="small" 
                            fullWidth
                            variant="contained"
                            onClick={() => handleJobClick(job)}
                          >
                            {t('viewDetails')}
                          </Button>
                          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<WhatsAppIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                shareOnWhatsApp(job);
                              }}
                              sx={{ flex: 1 }}
                            >
                              WhatsApp
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<EmailIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                shareViaEmail(job);
                              }}
                              sx={{ flex: 1 }}
                            >
                              Email
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="info"
                              startIcon={<TelegramIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                shareOnTelegram(job);
                              }}
                              sx={{ flex: 1 }}
                            >
                              Telegram
                            </Button>
                          </Box>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

        {/* Job Detail Modal (Single Scroll View) */}
        <Dialog
          open={!!selectedJob}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              m: isMobile ? 0 : 2,
              borderRadius: isMobile ? 0 : 3,
              maxHeight: isMobile ? '100vh' : '90vh',
            },
          }}
        >
          {selectedJob && (
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  px: { xs: 1, sm: 2 },
                  py: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'linear-gradient(120deg, rgba(102,126,234,0.08), rgba(17,122,201,0.14))',
                  borderBottom: '1px solid rgba(102,126,234,0.2)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <LanguageSwitcher variant="chips" />
                <IconButton
                  onClick={handleCloseModal}
                  sx={{
                    backgroundColor: 'rgba(17,24,39,0.72)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(17,24,39,0.9)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <DialogContent
                sx={{
                  p: 0,
                  overflowY: 'auto',
                  maxHeight: isMobile ? '100vh' : '90vh',
                }}
              >
                <Box sx={{ p: { xs: 2, sm: 3 } }}>
                  {(() => {
                    const importantDatesList = cleanListItems(selectedJob.importantDates);
                    const requiredDocsList = cleanListItems(selectedJob.requiredDocuments);
                    const syllabusList = cleanListItems(selectedJob.syllabus);
                    const resultDate =
                      selectedJob.resultInfo?.resultDate ||
                      selectedJob.resultsDate ||
                      null;
                    const hallTicketStart =
                      selectedJob.hallTicketDownloadStartingDate ||
                      selectedJob.hallTicketInfo?.downloadStartingDate ||
                      null;
                    const hallTicketDate =
                      selectedJob.hallTicketDownloadDate ||
                      selectedJob.hallTicketInfo?.downloadDate ||
                      null;

                    return (
                      <>
                  {/* Header */}
                  <Box
                    sx={{
                      mb: 2,
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 3,
                      background: 'linear-gradient(145deg, #e8eaf6 0%, #e3f2fd 50%, #f3e5f5 100%)',
                      border: '1px solid rgba(102,126,234,0.2)',
                      boxShadow: '0 4px 20px rgba(102,126,234,0.12)',
                    }}
                  >
                    <Stack direction="row" flexWrap="wrap" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <QualificationChips job={selectedJob} t={t} size="medium" />
                      {selectedJob.isUrgent && (
                        <Chip label={t('urgent')} color="error" sx={{ fontWeight: 'bold' }} />
                      )}
                    </Stack>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', mb: 0.75, lineHeight: 1.3 }}>
                      {translateJobContent(selectedJob.notificationName)}
                    </Typography>
                    <Typography variant={isMobile ? 'body1' : 'h6'} color="text.secondary" sx={{ fontWeight: 500 }}>
                      {translateJobContent(selectedJob.organization)}
                    </Typography>
                    {isMeaningfulText(selectedJob.qualification) && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1.5,
                          p: 1,
                          borderRadius: 1.5,
                          bgcolor: 'rgba(106,27,154,0.08)',
                          color: '#4a148c',
                          fontWeight: 600,
                        }}
                      >
                        <SchoolIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                        {t('qualification')}: {translateJobContent(selectedJob.qualification)}
                      </Typography>
                    )}
                  </Box>

                  {/* Key Information Grid */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {selectedJob.lastDate && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="lastDate" icon={CalendarTodayIcon}>
                          <LastDateDisplay
                            date={selectedJob.lastDate}
                            t={t}
                            formatDate={formatDate}
                            getDaysRemaining={getDaysRemaining}
                            variant="body1"
                          />
                        </InfoBox>
                      </Grid>
                    )}
                    {selectedJob.numberOfVacancies > 0 && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="vacancies" icon={WorkIcon}>
                          <Typography variant="body1">
                            <strong>{t('vacancies')}:</strong> {selectedJob.numberOfVacancies}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {isMeaningfulText(selectedJob.ageLimit) && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="ageLimit">
                          <Typography variant="body1">
                            <strong>{t('ageLimit')}:</strong> {translateJobContent(selectedJob.ageLimit)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {isMeaningfulText(selectedJob.applicationFee) && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="fee">
                          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                            <strong>{t('applicationFee')}:</strong>{' '}
                            {translateJobContent(selectedJob.applicationFee)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {isMeaningfulText(selectedJob.salary) && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="salary">
                          <Typography variant="body1">
                            <strong>{t('salary')}:</strong> {translateJobContent(selectedJob.salary)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {isMeaningfulText(selectedJob.location) && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="location" icon={LocationOnIcon}>
                          <Typography variant="body1">
                            <strong>{t('location')}:</strong> {translateJobContent(selectedJob.location)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {selectedJob.applicationStartDate && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="date" icon={CalendarTodayIcon}>
                          <Typography variant="body1">
                            <strong>{t('applicationStart') || 'Start Date'}:</strong> {formatDate(selectedJob.applicationStartDate)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {selectedJob.examDate && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="date" icon={CalendarTodayIcon}>
                          <Typography variant="body1">
                            <strong>{t('examDate') || 'Exam Date'}:</strong> {formatDate(selectedJob.examDate)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {resultDate && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="date" icon={CalendarTodayIcon}>
                          <Typography variant="body1">
                            <strong>{t('resultDate') || 'Result Date'}:</strong> {formatDate(resultDate)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {hallTicketStart && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="date" icon={CalendarTodayIcon}>
                          <Typography variant="body1">
                            <strong>Hall Ticket D/L From:</strong> {formatDate(hallTicketStart)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                    {hallTicketDate && (
                      <Grid item xs={12} sm={6}>
                        <InfoBox themeKey="date" icon={CalendarTodayIcon}>
                          <Typography variant="body1">
                            <strong>Hall Ticket Date:</strong> {formatDate(hallTicketDate)}
                          </Typography>
                        </InfoBox>
                      </Grid>
                    )}
                  </Grid>

                  {isMeaningfulText(selectedJob.description) && (
                    <DetailSection title={t('description')} color="#5c6bc0">
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translateJobContent(selectedJob.description)}
                      </Typography>
                    </DetailSection>
                  )}

                  {isMeaningfulText(selectedJob.remarks) && (
                    <DetailSection title="Remarks" color="#8e24aa">
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translateJobContent(selectedJob.remarks)}
                      </Typography>
                    </DetailSection>
                  )}

                  {isMeaningfulText(selectedJob.eligibility) && (
                    <DetailSection title={t('eligibility')} color="#2e7d32">
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translateJobContent(selectedJob.eligibility)}
                      </Typography>
                    </DetailSection>
                  )}

                  {isMeaningfulText(selectedJob.applicationProcess) && (
                    <DetailSection title="Application Process" color="#ef6c00">
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {translateJobContent(selectedJob.applicationProcess)}
                      </Typography>
                    </DetailSection>
                  )}

                  {importantDatesList.length > 0 && (
                    <DetailSection title={t('importantDates')} color="#0277bd">
                      <Stack spacing={0.75}>
                        {importantDatesList.map((date, idx) => (
                          <Typography
                            key={idx}
                            variant="body1"
                            sx={{
                              pl: 1.5,
                              borderLeft: '3px solid #29b6f6',
                              py: 0.5,
                            }}
                          >
                            {date}
                          </Typography>
                        ))}
                      </Stack>
                    </DetailSection>
                  )}

                  {requiredDocsList.length > 0 && (
                    <DetailSection title={t('requiredDocuments')} color="#6a1b9a">
                      <Stack spacing={0.75}>
                        {requiredDocsList.map((doc, idx) => (
                          <Typography
                            key={idx}
                            variant="body1"
                            sx={{
                              pl: 1.5,
                              borderLeft: '3px solid #ab47bc',
                              py: 0.5,
                            }}
                          >
                            {translateJobContent(doc)}
                          </Typography>
                        ))}
                      </Stack>
                    </DetailSection>
                  )}

                  {syllabusList.length > 0 && (
                    <DetailSection title="Syllabus" color="#00838f">
                      <Stack spacing={0.75}>
                        {syllabusList.map((topic, idx) => (
                          <Typography
                            key={idx}
                            variant="body1"
                            sx={{
                              pl: 1.5,
                              borderLeft: '3px solid #26c6da',
                              py: 0.5,
                            }}
                          >
                            {translateJobContent(topic)}
                          </Typography>
                        ))}
                      </Stack>
                    </DetailSection>
                  )}
                      </>
                    );
                  })()}

                  {/* Share Buttons */}
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {t('share') || 'Share'}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      onClick={() => shareOnWhatsApp(selectedJob)}
                      sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<EmailIcon />}
                      onClick={() => shareViaEmail(selectedJob)}
                      sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                      Email
                    </Button>
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<TelegramIcon />}
                      onClick={() => shareOnTelegram(selectedJob)}
                      sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: { xs: '100%', sm: 'auto' } }}
                    >
                      Telegram
                    </Button>
                  </Stack>

                  {/* Action Buttons */}
                  <Divider sx={{ my: 3 }} />
                  <Stack direction="row" spacing={2} sx={{ mt: 3, flexWrap: 'wrap' }}>
                    {selectedJob.notificationUrl && (
                      <Button
                        variant="outlined"
                        color="primary"
                        href={selectedJob.notificationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewIcon />}
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: { xs: '100%', sm: 'auto' } }}
                      >
                        {t('viewNotification')}
                      </Button>
                    )}
                    {selectedJob.officialWebsite && (
                      <Button
                        variant="outlined"
                        href={selectedJob.officialWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<OpenInNewIcon />}
                        sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto' }, minWidth: { xs: '100%', sm: 'auto' } }}
                      >
                        {t('officialWebsite')}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </DialogContent>
            </Box>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default PublicJobAlerts;

